import { useRef, useState } from "react";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getVideoUrl, getImageUrl } from "../utils/productMedia";
import { colors } from "../utils/theme";

const ProductCard = ({ product, variant, onNavigate, onAddToCart, onToggleFavorite, onRemove, isWishlisted, onBuyProduct }) => {
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const videoRef = useRef(null);
  const playAttempted = useRef(false);

  const productId = product._id || product.id || product.productId;
  const imageUrl = getImageUrl(product);
  const videoUrl = getVideoUrl(product);

  const handleMouseEnter = () => {
    setHovering(true);
    playAttempted.current = false;
  };

  const handleMouseLeave = () => {
    setHovering(false);
    const v = videoRef.current;
    if (v) v.pause();
  };

  const handleToggleAudio = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (v) {
      v.muted = !v.muted;
      setAudioEnabled(!v.muted);
    }
  };

  // Play video when it mounts after hover
  const setVideoRef = (el) => {
    videoRef.current = el;
    if (el && hovering && !playAttempted.current) {
      playAttempted.current = true;
      el.muted = !audioEnabled;
      el.play().catch(() => {});
    }
  };

  const isBuyAgain = variant === "buy-again";
  const isWishlist = variant === "wishlist";
  const isBrowseHistory = variant === "browse-history";
  const showTwoButtons = isWishlist || isBrowseHistory || isBuyAgain;

  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: colors.outlineVariant, bgcolor: colors.surfaceContainerLow, transition: "transform 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}>
      <Box sx={{ position: "relative", width: "100%", paddingTop: "100%", bgcolor: "#fff", cursor: "pointer", overflow: "hidden" }}
        onClick={() => onNavigate && onNavigate(productId)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        {videoUrl && hovering ? (
          <Box component="video" ref={setVideoRef} src={videoUrl} muted loop playsInline
            sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (videoUrl || imageUrl) ? (
          <Box component="img" src={imageUrl} alt={product.title} loading="lazy"
            sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 40, opacity: 0.3 }}>📦</Box>
        )}

        {/* Audio button always visible if product has video */}
        {videoUrl && (
          <IconButton size="small" onClick={handleToggleAudio}
            sx={{ position: "absolute", top: 6, right: 6, zIndex: 10, backgroundColor: "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }}>
            {audioEnabled ? <VolumeUpIcon sx={{ fontSize: 16 }} /> : <VolumeOffIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        )}

        {/* Overlay icons - audio at top, then wishlist/delete below */}
        {onRemove && (
          <IconButton size="small" sx={{ position: "absolute", top: 52, right: 6, zIndex: 1, bgcolor: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "#fff" } }}
            onClick={(e) => { e.stopPropagation(); onRemove(productId); }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {onToggleFavorite && (
          <Box sx={{ position: "absolute", top: onRemove ? 98 : 52, right: 6 }}>
            {isWishlisted ? (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleFavorite(product); }}
                sx={{ bgcolor: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "#fff" } }}>
                <FavoriteIcon sx={{ fontSize: 16, color: colors.error }} />
              </IconButton>
            ) : (
              <Tooltip title="Add to your Wishlist">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleFavorite(product); }}
                  sx={{ bgcolor: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "#fff" } }}>
                  <FavoriteBorderIcon sx={{ fontSize: 16, color: colors.error }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textMain, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {product.title}
        </Typography>
        <Typography sx={{ color: colors.primary, fontWeight: 700, fontSize: 15, mt: 0.5, mb: 1 }}>
          ₹{Number(product.price || 0).toLocaleString("en-IN")}
        </Typography>
        {isBrowseHistory && product.originalPrice && product.originalPrice > product.price && (
          <Typography sx={{ color: colors.textMuted, fontSize: 11, textDecoration: "line-through", mb: 1 }}>
            ₹{Number(product.originalPrice).toLocaleString("en-IN")}
          </Typography>
        )}
        {showTwoButtons ? (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Button size="small" variant="contained" fullWidth onClick={() => onAddToCart && onAddToCart(product)}
              sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 11, minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}>
              <ShoppingBagIcon sx={{ fontSize: 13, mr: 0.3 }} /> Cart
            </Button>
            <Button size="small" variant="contained" fullWidth onClick={() => onBuyProduct && onBuyProduct(productId)}
              sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 11, minHeight: 28, "&:hover": { bgcolor: "#e6c512" } }}>
              Buy
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ProductCard;